import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MemeNewsChart from './MemeNewsChart';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const STORAGE_TOKEN_LEGACY = 'xxbs_info_token';
const STORAGE_SKILL_TOKEN = 'xxbs_skills_token';
const STORAGE_STOCK_TOKEN = 'xxbs_stock_token';
const WINDOWS = ['10m', '1h', '4h', '24h', '72h'];
const WINDOWS_WITH_ALL = [...WINDOWS, 'all'];
const PRESET_APIS = [
  { api_name: 'daily', title: 'A股日线行情', sample: { ts_code: '000001.SZ', start_date: '20260101', end_date: '20260110' } },
  { api_name: 'daily_basic', title: '每日指标', sample: { ts_code: '000001.SZ', start_date: '20260101', end_date: '20260110' } },
  { api_name: 'stock_basic', title: '股票列表', sample: { list_status: 'L', limit: 20 } },
  { api_name: 'moneyflow', title: '个股资金流向', sample: { ts_code: '000001.SZ', start_date: '20260101', end_date: '20260110' } },
  { api_name: 'index_daily', title: '指数日线行情', sample: { ts_code: '000001.SH', start_date: '20260101', end_date: '20260110' } },
  { api_name: 'us_daily', title: '美股行情', sample: { ts_code: 'AAPL', start_date: '20260701', end_date: '20260720' } },
  { api_name: 'fund_daily', title: '场内基金日线', sample: { ts_code: '510330.SH', start_date: '20260101', end_date: '20260110' } },
  { api_name: 'top_list', title: '龙虎榜每日明细', sample: { trade_date: '20260106' } },
];

function readStorage(key) {
  try {
    return String(localStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
}

function writeStorage(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (_) {}
}

function loadSkillToken() {
  return readStorage(STORAGE_SKILL_TOKEN) || readStorage(STORAGE_TOKEN_LEGACY);
}

function loadStockToken() {
  return readStorage(STORAGE_STOCK_TOKEN);
}

function asList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  // 解开代理层 / skill 层：{ success, data } 可能套两层
  if (payload && typeof payload === 'object' && payload.data != null) {
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.success != null || payload.timestamp != null || payload.error != null) {
      const nested = asList(payload.data);
      if (nested.length) return nested;
    }
  }

  const candidates = [
    payload.items,
    payload.results,
    payload.list,
    payload.news,
    payload.records,
    payload.summaries,
    payload.rows,
    payload.entries,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }

  // data 为对象时再向下找一层常见列表字段
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    const nested = asList(payload.data);
    if (nested.length) return nested;
  }
  return [];
}

function stockRows(payload) {
  if (!payload) return [];

  // 兼容：代理返回 {code,msg,data:{fields,items}} 或直接 {fields,items} 或 data 为对象数组
  let node = payload;
  if (node && node.data != null && (node.fields == null) && (node.code != null || node.msg != null || node.success != null)) {
    node = node.data;
  }
  if (node && node.data != null && Array.isArray(node.data?.fields) && Array.isArray(node.data?.items)) {
    node = node.data;
  }

  if (node && Array.isArray(node.fields) && Array.isArray(node.items)) {
    return node.items.map((row) => {
      if (row && typeof row === 'object' && !Array.isArray(row)) return row;
      const obj = {};
      node.fields.forEach((f, i) => {
        obj[String(f)] = Array.isArray(row) ? row[i] : row?.[f];
      });
      return obj;
    });
  }

  if (Array.isArray(node)) {
    if (node.length && Array.isArray(node[0])) return []; // 缺 fields，无法映射
    return node;
  }
  if (Array.isArray(node?.items)) {
    if (node.items.length && typeof node.items[0] === 'object' && !Array.isArray(node.items[0])) {
      return node.items;
    }
  }
  if (Array.isArray(node?.rows)) return node.rows;
  return [];
}

function stockFields(rows, payload) {
  const nodes = [payload, payload?.data, payload?.data?.data];
  for (const n of nodes) {
    if (Array.isArray(n?.fields) && n.fields.length) return n.fields.map(String);
  }
  if (!rows.length) return [];
  const first = rows[0];
  if (first && typeof first === 'object' && !Array.isArray(first)) return Object.keys(first);
  return [];
}

function pickText(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

function formatTime(value) {
  if (value == null || value === '') return '-';
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('zh-CN', { hour12: false });
  }
  const s = String(value);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime()) && /[-:T]/.test(s)) {
    return d.toLocaleString('zh-CN', { hour12: false });
  }
  return s;
}

function newsTitle(item) {
  return pickText(item?.title, item?.title_cn, item?.title_en, item?.headline, item?.name, item?.question, '无标题');
}

function newsBody(item) {
  return pickText(item?.summary, item?.description, item?.desc, item?.content, item?.translation, item?.text, item?.body);
}

function newsMeta(item) {
  const parts = [
    pickText(item?.source, item?.channel, item?.from, item?.source_group),
    formatTime(item?.published_at || item?.publish_time || item?.created_at || item?.time || item?.ts || item?.datetime),
    pickText(item?.lang, item?.language),
  ].filter((x) => x && x !== '-');
  return parts.join(' · ');
}

export default function GlobalInfoPage() {
  const [skillInput, setSkillInput] = useState(() => loadSkillToken());
  const [stockInput, setStockInput] = useState(() => loadStockToken());
  const [skillToken, setSkillToken] = useState(() => loadSkillToken());
  const [stockToken, setStockToken] = useState(() => loadStockToken());
  const [tokenSavedAt, setTokenSavedAt] = useState('');
  const [board, setBoard] = useState('flash'); // flash | quant | usage
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState('');

  // 闪讯
  const [windowVal, setWindowVal] = useState('1h');
  const [lang, setLang] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [stockQ, setStockQ] = useState('');
  const [cryptoToken, setCryptoToken] = useState('');
  const [limit, setLimit] = useState(20);
  const [cryptoNews, setCryptoNews] = useState([]);
  const [marketNews, setMarketNews] = useState([]);
  const [importantNews, setImportantNews] = useState([]);
  const [chatSummaries, setChatSummaries] = useState([]);
  const [summaryGroup, setSummaryGroup] = useState('');
  const [channels, setChannels] = useState(null);
  const [caType, setCaType] = useState('ca');
  const [caQuery, setCaQuery] = useState('');
  const [caGroup, setCaGroup] = useState('');
  const [caRecords, setCaRecords] = useState([]);
  const [usage, setUsage] = useState(null);
  const [stockUsage, setStockUsage] = useState(null);
  const [feishuEnabled, setFeishuEnabled] = useState(true);
  const [feishuWebhook, setFeishuWebhook] = useState('');
  const [feishuSecret, setFeishuSecret] = useState('');
  const [feishuMeta, setFeishuMeta] = useState({});

  // 量化
  const [catalog, setCatalog] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogQ, setCatalogQ] = useState('');
  const [apiName, setApiName] = useState('daily');
  const [paramsText, setParamsText] = useState(JSON.stringify(PRESET_APIS[0].sample, null, 2));
  const [stockResult, setStockResult] = useState(null);
  const [stockError, setStockError] = useState('');

  const skillReady = !!skillToken.trim();
  const stockReady = !!stockToken.trim();

  const skillHeaders = useMemo(() => ({
    'X-XXBS-Skill-Token': skillToken.trim(),
    Accept: 'application/json',
  }), [skillToken]);

  const stockHeaders = useMemo(() => ({
    'X-XXBS-Stock-Token': stockToken.trim(),
    Accept: 'application/json',
  }), [stockToken]);

  const handleSaveTokens = () => {
    const nextSkill = skillInput.trim();
    const nextStock = stockInput.trim();
    writeStorage(STORAGE_SKILL_TOKEN, nextSkill);
    writeStorage(STORAGE_STOCK_TOKEN, nextStock);
    writeStorage(STORAGE_TOKEN_LEGACY, ''); // 旧单 Token 作废，避免继续混用
    setSkillToken(nextSkill);
    setStockToken(nextStock);
    setTokenSavedAt(new Date().toLocaleString('zh-CN', { hour12: false }));
    setMessage(
      nextSkill || nextStock
        ? `已保存：闪讯 ${nextSkill ? '✓' : '未填'} · 量化 ${nextStock ? '✓' : '未填'}（两套 Token 互相独立，勿混用）`
        : '已清除本机 Token'
    );
  };

  const handleClearTokens = () => {
    setSkillInput('');
    setStockInput('');
    writeStorage(STORAGE_SKILL_TOKEN, '');
    writeStorage(STORAGE_STOCK_TOKEN, '');
    writeStorage(STORAGE_TOKEN_LEGACY, '');
    setSkillToken('');
    setStockToken('');
    setMessage('已清除本机闪讯 / 量化 Token');
  };

  const skillGet = useCallback(async (skillPath, params = {}) => {
    if (!skillToken.trim()) throw new Error('请先填写并保存闪讯 Token（skill.xxbs.club）');
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v == null || v === '') return;
      qs.set(k, String(v));
    });
    const url = api(`/api/xxbs/skill${skillPath}${qs.toString() ? `?${qs}` : ''}`);
    const resp = await fetch(url, { headers: skillHeaders, cache: 'no-store' });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok || body.success === false) {
      throw new Error(body?.message || body?.error || `闪讯请求失败 ${resp.status}`);
    }
    // 代理返回 { success, data: <上游整包> }；上游也可能再包一层 { success, data }
    let payload = body.data;
    if (payload && typeof payload === 'object' && payload.success != null && payload.data != null) {
      payload = payload.data;
    }
    return payload;
  }, [skillToken, skillHeaders]);

  const runLabeled = async (label, fn) => {
    setLoading(label);
    setMessage('');
    try {
      await fn();
    } catch (err) {
      setMessage(err?.message || '请求失败');
    } finally {
      setLoading('');
    }
  };

  const loadFeishuForwardConfig = useCallback(async () => {
    try {
      const resp = await fetch(api('/api/chat-summary-feishu/config'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) return;
      const data = body.data || {};
      setFeishuEnabled(data.enabled !== false);
      setFeishuWebhook(data.hasWebhook ? '' : (data.webhookUrl || ''));
      setFeishuSecret('');
      setFeishuMeta({
        hasWebhook: !!data.hasWebhook,
        hasSkillToken: !!data.hasSkillToken,
        hasWebhookSecret: !!data.hasWebhookSecret,
        cron: data.cron || '',
        lastResult: data.lastResult || null,
        lastSentKey: data.lastSentKey || '',
      });
      // 若服务端已有 webhook，输入框用占位提示而不回显完整 URL
      if (data.hasWebhook) setFeishuWebhook('');
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadFeishuForwardConfig();
  }, [loadFeishuForwardConfig]);

  const saveFeishuForward = async () => {
    await runLabeled('保存飞书转发', async () => {
      const payload = {
        enabled: feishuEnabled,
        skillToken: skillToken.trim() || skillInput.trim(),
      };
      if (feishuWebhook.trim()) payload.webhookUrl = feishuWebhook.trim();
      if (feishuSecret.trim()) payload.webhookSecret = feishuSecret.trim();
      // 用户清空并想删除 webhook：当本机显示为空且点了保存且 meta 曾有 webhook 时，需显式传空——仅当输入了空且用户改过。简单策略：有输入才更新 URL。
      const resp = await fetch(api('/api/chat-summary-feishu/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) {
        throw new Error(body?.message || `保存失败 ${resp.status}`);
      }
      setFeishuWebhook('');
      setFeishuSecret('');
      await loadFeishuForwardConfig();
      setMessage(body.message || '已保存飞书整点转发配置（Skill Token 已同步到服务端定时任务）');
    });
  };

  const testFeishuForward = async () => {
    await runLabeled('试发飞书摘要', async () => {
      // 试发前若有新 webhook / token，先保存
      if (feishuWebhook.trim() || skillToken.trim() || skillInput.trim()) {
        const payload = {
          enabled: feishuEnabled,
          skillToken: skillToken.trim() || skillInput.trim(),
        };
        if (feishuWebhook.trim()) payload.webhookUrl = feishuWebhook.trim();
        if (feishuSecret.trim()) payload.webhookSecret = feishuSecret.trim();
        await fetch(api('/api/chat-summary-feishu/config'), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const resp = await fetch(api('/api/chat-summary-feishu/test'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) {
        throw new Error(body?.message || body?.data?.message || `试发失败 ${resp.status}`);
      }
      await loadFeishuForwardConfig();
      setMessage(body.message || body?.data?.message || '已试发到飞书');
    });
  };

  const refreshFlashBoard = async () => {
    await runLabeled('闪讯看板', async () => {
      const win = windowVal === 'all' ? '24h' : windowVal;
      // 无筛选时走 recent；有关键词/股票/加密标签时走 search
      const useCryptoSearch = !!(keyword.trim() || cryptoToken.trim());
      const useMarketSearch = !!(keyword.trim() || stockQ.trim());
      const cryptoReq = useCryptoSearch
        ? skillGet('/crypto_news/search', {
          window: windowVal,
          lang,
          token: cryptoToken.trim() || undefined,
          keyword: keyword.trim() || undefined,
          limit,
        })
        : skillGet('/crypto_news/recent', { window: win, lang, limit });
      const marketReq = useMarketSearch
        ? skillGet('/news/search', {
          window: windowVal,
          keyword: keyword.trim() || undefined,
          stock: stockQ.trim() || undefined,
          limit,
        })
        : skillGet('/news/recent', { window: win, limit });

      const [crypto, market, important, chats, meta] = await Promise.all([
        cryptoReq,
        marketReq,
        skillGet('/news/important', {
          window: win,
          keyword: keyword.trim() || undefined,
          stock: stockQ.trim() || undefined,
          limit,
        }),
        skillGet('/chat_summaries', {
          window: win,
          group: summaryGroup || undefined,
          limit: Math.min(Number(limit) || 20, 24),
        }),
        skillGet('/meta/channels'),
      ]);
      const cryptoList = asList(crypto);
      const marketList = asList(market);
      const importantList = asList(important);
      const chatList = dedupeChatSummaries(asList(chats));
      setCryptoNews(cryptoList);
      setMarketNews(marketList);
      setImportantNews(importantList);
      setChatSummaries(chatList);
      setChannels(meta);
      const total = cryptoList.length + marketList.length + importantList.length + chatList.length;
      if (total === 0) {
        setMessage(`接口已通，但当前筛选下无数据（窗口 ${windowVal}${keyword ? ` · 关键词 ${keyword}` : ''}）。可改成 24h/72h，或清空关键词后再刷新。`);
      } else {
        setMessage(`已刷新：加密 ${cryptoList.length} · 市场 ${marketList.length} · 重要 ${importantList.length} · 摘要 ${chatList.length}`);
      }
    });
  };

  const searchCrypto = async () => {
    await runLabeled('加密新闻搜索', async () => {
      const data = await skillGet('/crypto_news/search', {
        window: windowVal,
        lang,
        token: cryptoToken,
        keyword,
        limit,
      });
      setCryptoNews(asList(data));
    });
  };

  const searchMarket = async () => {
    await runLabeled('市场新闻搜索', async () => {
      const data = await skillGet('/news/search', {
        window: windowVal,
        keyword,
        stock: stockQ,
        limit,
      });
      setMarketNews(asList(data));
      if (data?.stock_analyses) {
        setMessage(`已聚合个股分析 ${Object.keys(data.stock_analyses).length} 项`);
      }
    });
  };

  const searchCa = async () => {
    await runLabeled('群聊记录搜索', async () => {
      if (!caQuery.trim()) throw new Error('请填写合约地址或 URL');
      const data = await skillGet('/chat_records/search', {
        type: caType,
        query: caQuery.trim(),
        group: caGroup || undefined,
        limit,
      });
      setCaRecords(asList(data));
      const n = asList(data).length;
      setMessage(n ? `群聊记录命中 ${n} 条` : '未找到匹配的 CA / URL 记录');
    });
  };

  const refreshChatSummaries = async () => {
    await runLabeled('群聊摘要', async () => {
      const win = windowVal === 'all' ? '24h' : windowVal;
      const data = await skillGet('/chat_summaries', {
        window: win,
        group: summaryGroup || undefined,
        limit: Math.min(Number(limit) || 20, 24),
      });
      // 顺带刷新渠道目录，方便选群
      try {
        const meta = await skillGet('/meta/channels');
        setChannels(meta);
      } catch (_) { /* 忽略 */ }
      const list = dedupeChatSummaries(asList(data));
      setChatSummaries(list);
      setMessage(list.length
        ? `已刷新群聊摘要 ${list.length} 条${summaryGroup ? ` · 群 ${summaryGroup}` : ''}`
        : `当前窗口 ${win} 暂无群聊摘要${summaryGroup ? `（群 ${summaryGroup}）` : ''}，可改成 24h/72h 或换群再试`);
    });
  };

  const loadUsage = async () => {
    await runLabeled('用量查询', async () => {
      const tasks = [];
      if (skillReady) {
        tasks.push(
          skillGet('/token/usage')
            .then((data) => setUsage(data))
            .catch((err) => { setUsage({ error: err.message }); throw err; })
        );
      } else {
        setUsage(null);
      }
      if (stockReady) {
        tasks.push((async () => {
          const resp = await fetch(api('/api/xxbs/stock/usage'), { headers: stockHeaders, cache: 'no-store' });
          const body = await resp.json().catch(() => ({}));
          if (!resp.ok || body.success === false) {
            setStockUsage({ error: body?.message || `量化用量失败 ${resp.status}` });
            throw new Error(body?.message || `量化用量失败 ${resp.status}`);
          }
          setStockUsage(body.data);
        })());
      } else {
        setStockUsage(null);
      }
      if (!tasks.length) throw new Error('请先保存闪讯 Token 和/或量化 Token');
      const results = await Promise.allSettled(tasks);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === results.length) {
        throw new Error(failed[0].reason?.message || '用量查询失败');
      }
      if (failed.length) {
        setMessage(failed.map((f) => f.reason?.message || '部分用量失败').join('；'));
      }
    });
  };

  const loadCatalog = async () => {
    if (catalogLoaded && catalog.length) return;
    await runLabeled('接口目录', async () => {
      const resp = await fetch(api('/api/xxbs/stock/docs'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || '拉取量化接口目录失败');
      const items = Array.isArray(body?.data?.items) ? body.data.items : [];
      setCatalog(items);
      setCatalogLoaded(true);
    });
  };

  useEffect(() => {
    if (board === 'quant') loadCatalog();
    if (board === 'usage' && (skillReady || stockReady)) loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  const filteredCatalog = useMemo(() => {
    const q = catalogQ.trim().toLowerCase();
    if (!q) return catalog.slice(0, 80);
    return catalog.filter((it) => {
      const hay = [it.api_name, it.title, it.category, it.description].join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 80);
  }, [catalog, catalogQ]);

  const applyPreset = (preset) => {
    setApiName(preset.api_name);
    setParamsText(JSON.stringify(preset.sample || {}, null, 2));
  };

  const applyCatalogItem = (item) => {
    setApiName(item.api_name);
    setParamsText(JSON.stringify(item.sample_params || {}, null, 2));
  };

  const runStockQuery = async () => {
    await runLabeled('量化查询', async () => {
      if (!stockToken.trim()) throw new Error('请先填写并保存量化 Token（stock-data.xxbs.club）');
      let params = {};
      try {
        params = JSON.parse(paramsText || '{}');
      } catch {
        throw new Error('参数 JSON 格式不正确');
      }
      if (!params || typeof params !== 'object' || Array.isArray(params)) {
        throw new Error('params 必须是 JSON 对象');
      }
      const resp = await fetch(api('/api/xxbs/stock'), {
        method: 'POST',
        headers: {
          ...stockHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ api_name: apiName.trim(), params }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) {
        setStockResult(null);
        setStockError(body?.message || `查询失败 ${resp.status}`);
        throw new Error(body?.message || `查询失败 ${resp.status}`);
      }
      setStockError('');
      setStockResult(body.data);
    });
  };

  const rows = stockRows(stockResult);
  const fields = stockFields(rows, stockResult);
  const chatGroups = Array.isArray(channels?.chat_groups) ? channels.chat_groups : [];

  return (
    <div className="global-info-page">
      <section className="global-info-hero">
        <div>
          <span className="global-info-eyebrow">XXBS Global Board</span>
          <h1>全球信息</h1>
          <p>
            闪讯新闻与量化行情一站看板。两套服务各有独立 Token，仅保存在本机浏览器。
          </p>
        </div>
        <div className="global-info-token-card">
          <div className="global-info-token-note">
            闪讯与量化是<strong>两套独立 Token</strong>，分别来自 skill / stock-data，不能混用。
          </div>
          <label>
            闪讯 Token（skill.xxbs.club）
            <input
              type="password"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              placeholder="Header: X-API-Token"
              autoComplete="off"
            />
          </label>
          <label>
            量化 Token（stock-data.xxbs.club）
            <input
              type="password"
              value={stockInput}
              onChange={(e) => setStockInput(e.target.value)}
              placeholder="POST body token / Bearer"
              autoComplete="off"
            />
          </label>
          <div className="global-info-token-actions">
            <button type="button" className="global-info-btn" onClick={handleSaveTokens}>保存 Token</button>
            <button type="button" className="global-info-btn ghost" onClick={handleClearTokens}>清除</button>
          </div>
          <small>
            闪讯 {skillReady ? '已就绪' : '未填'} · 量化 {stockReady ? '已就绪' : '未填'}
            {tokenSavedAt ? ` · 保存于 ${tokenSavedAt}` : ''}
          </small>
        </div>
      </section>

      <div className="global-info-subtabs">
        <button type="button" className={board === 'flash' ? 'active' : ''} onClick={() => setBoard('flash')}>闪讯看板</button>
        <button type="button" className={board === 'meme' ? 'active' : ''} onClick={() => setBoard('meme')}>Meme K线</button>
        <button type="button" className={board === 'quant' ? 'active' : ''} onClick={() => setBoard('quant')}>量化行情</button>
        <button type="button" className={board === 'usage' ? 'active' : ''} onClick={() => setBoard('usage')}>用量查询</button>
      </div>

      {(message || loading) && (
        <div className={`global-info-message ${loading ? 'is-loading' : ''}`}>
          {loading ? `正在加载：${loading}…` : message}
        </div>
      )}

      {board === 'flash' && (
        <>
          <section className="global-info-panel">
            <div className="global-info-panel-head">
              <div>
                <h2>筛选与刷新</h2>
                <p>默认拉取最近窗口内的加密新闻、市场新闻、重要新闻与群聊摘要。</p>
              </div>
              <button type="button" className="global-info-btn" disabled={!skillReady || !!loading} onClick={refreshFlashBoard}>
                一键刷新看板
              </button>
            </div>
            <div className="global-info-filters">
              <label>
                时间窗口
                <select value={windowVal} onChange={(e) => setWindowVal(e.target.value)}>
                  {WINDOWS_WITH_ALL.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
              <label>
                语言
                <select value={lang} onChange={(e) => setLang(e.target.value)}>
                  <option value="all">all</option>
                  <option value="cn">cn</option>
                  <option value="en">en</option>
                </select>
              </label>
              <label>
                条数
                <input type="number" min={1} max={100} value={limit} onChange={(e) => setLimit(e.target.value)} />
              </label>
              <label>
                关键词
                <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="标题/描述/来源" />
              </label>
              <label>
                股票
                <input value={stockQ} onChange={(e) => setStockQ(e.target.value)} placeholder="如 贵州茅台" />
              </label>
              <label>
                加密 Token
                <input value={cryptoToken} onChange={(e) => setCryptoToken(e.target.value)} placeholder="BTC / ETH" />
              </label>
            </div>
            <div className="global-info-actions">
              <button type="button" className="global-info-btn soft" disabled={!skillReady || !!loading} onClick={searchCrypto}>搜索加密新闻</button>
              <button type="button" className="global-info-btn soft" disabled={!skillReady || !!loading} onClick={searchMarket}>搜索市场新闻</button>
            </div>
          </section>

          <div className="global-info-columns">
            <NewsColumn title="加密新闻" items={cryptoNews} empty="暂无加密新闻" />
            <NewsColumn title="市场新闻" items={marketNews} empty="暂无市场新闻" />
            <NewsColumn title="重要新闻" items={importantNews} empty="暂无重要新闻" tone="important" />
          </div>

          <section className="global-info-panel">
            <div className="global-info-panel-head">
              <div>
                <h2>群聊摘要</h2>
                <p>AI 小时级总结。可按群名筛选；上游不支持关键词全文搜索，只能按时间窗口 + 群拉取。</p>
              </div>
              <button type="button" className="global-info-btn" disabled={!skillReady || !!loading} onClick={refreshChatSummaries}>
                刷新摘要
              </button>
            </div>
            <div className="global-info-filters">
              <label>
                群名
                <select value={summaryGroup} onChange={(e) => setSummaryGroup(e.target.value)}>
                  <option value="">全部</option>
                  {chatGroups.map((g) => {
                    const name = typeof g === 'string' ? g : (g?.name || g?.group || '');
                    return name ? <option key={name} value={name}>{name}</option> : null;
                  })}
                </select>
              </label>
              <label>
                时间窗口
                <select value={windowVal} onChange={(e) => setWindowVal(e.target.value)}>
                  {WINDOWS_WITH_ALL.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
            </div>
            {chatSummaries.length === 0 ? (
              <div className="global-info-empty">
                {chatGroups.length
                  ? '暂无群聊摘要。可点「刷新摘要」，或把窗口改成 24h/72h；群列表来自渠道目录。'
                  : '暂无群聊摘要。先点「一键刷新看板」或「刷新摘要」拉取渠道与摘要。'}
              </div>
            ) : (
              <div className="global-info-summary-grid">
                {chatSummaries.map((item, idx) => (
                  <ChatSummaryCard key={item.id || item.group_name || item.group || idx} item={item} index={idx} />
                ))}
              </div>
            )}
          </section>

          <section className="global-info-panel">
            <div className="global-info-panel-head">
              <div>
                <h2>飞书整点转发</h2>
                <p>
                  Cloudflare 每小时第 1 分钟（上海时区）把「当前整点」摘要发到飞书群。例如 6:01 发 6 点、7:01 发 7 点。
                  需保存 Webhook；Skill Token 会写入服务端供定时任务使用。
                </p>
              </div>
              <div className="global-info-actions" style={{ gap: 8 }}>
                <button type="button" className="global-info-btn soft" disabled={!!loading} onClick={saveFeishuForward}>
                  保存配置
                </button>
                <button type="button" className="global-info-btn" disabled={!!loading} onClick={testFeishuForward}>
                  立即试发
                </button>
              </div>
            </div>
            <div className="global-info-filters">
              <label className="global-info-check">
                <input
                  type="checkbox"
                  checked={feishuEnabled}
                  onChange={(e) => setFeishuEnabled(e.target.checked)}
                />
                启用整点转发
              </label>
              <label>
                飞书机器人 Webhook
                <input
                  type="password"
                  value={feishuWebhook}
                  onChange={(e) => setFeishuWebhook(e.target.value)}
                  placeholder={feishuMeta.hasWebhook ? '已保存，填写新地址可覆盖' : 'https://open.feishu.cn/open-apis/bot/v2/hook/...'}
                  autoComplete="off"
                />
              </label>
              <label>
                签名校验 Secret（可选）
                <input
                  type="password"
                  value={feishuSecret}
                  onChange={(e) => setFeishuSecret(e.target.value)}
                  placeholder={feishuMeta.hasWebhookSecret ? '已保存，填写新 Secret 可覆盖' : '未开启签名可留空'}
                  autoComplete="off"
                />
              </label>
            </div>
            <small className="global-info-feishu-meta">
              {feishuMeta.hasWebhook ? 'Webhook 已配置' : 'Webhook 未配置'}
              {' · '}
              {feishuMeta.hasSkillToken || skillReady ? 'Skill Token 可用' : '缺少 Skill Token'}
              {feishuMeta.lastResult?.message ? ` · 上次：${feishuMeta.lastResult.message}` : ''}
              {feishuMeta.cron ? ` · Cron ${feishuMeta.cron}` : ''}
            </small>
          </section>

          <section className="global-info-panel">
            <div className="global-info-panel-head">
              <div>
                <h2>群聊记录搜索（仅 CA / URL）</h2>
                <p>搜合约地址或链接，结果按对话气泡展示上下文，代币信息用卡片呈现。</p>
              </div>
              <button type="button" className="global-info-btn" disabled={!skillReady || !!loading} onClick={searchCa}>搜索</button>
            </div>
            <div className="global-info-filters">
              <label>
                类型
                <select value={caType} onChange={(e) => setCaType(e.target.value)}>
                  <option value="ca">合约地址 CA</option>
                  <option value="url">链接 URL</option>
                </select>
              </label>
              <label>
                查询
                <input value={caQuery} onChange={(e) => setCaQuery(e.target.value)} placeholder="0x... 或 https://..." />
              </label>
              <label>
                群名
                <select value={caGroup} onChange={(e) => setCaGroup(e.target.value)}>
                  <option value="">全部</option>
                  {chatGroups.map((g) => {
                    const name = typeof g === 'string' ? g : (g?.name || g?.group || '');
                    return name ? <option key={name} value={name}>{name}</option> : null;
                  })}
                </select>
              </label>
            </div>
            {caRecords.length === 0 ? (
              <div className="global-info-empty">暂无搜索结果</div>
            ) : (
              <div className="chat-hit-list">
                {caRecords.map((item, idx) => (
                  <ChatRecordHit key={item.id || `${item.query}-${idx}`} item={item} index={idx} />
                ))}
              </div>
            )}
          </section>

          {channels && (
            <section className="global-info-panel">
              <div className="global-info-panel-head">
                <div>
                  <h2>渠道目录</h2>
                  <p>来自 /meta/channels，勿写死渠道名。</p>
                </div>
              </div>
              <pre className="global-info-json">{JSON.stringify(channels, null, 2)}</pre>
            </section>
          )}
        </>
      )}

      {board === 'meme' && (
        <MemeNewsChart
          skillReady={skillReady}
          skillGet={skillGet}
          limit={limit}
          loading={loading}
          runLabeled={runLabeled}
          onMessage={setMessage}
        />
      )}

      {board === 'quant' && (
        <>
          <section className="global-info-panel">
            <div className="global-info-panel-head">
              <div>
                <h2>量化数据查询</h2>
                <p>POST `api_name` + `params` 到 stock-data.xxbs.club（需<strong>量化 Token</strong>），目录共 {catalog.length || '—'} 个可用接口。</p>
              </div>
              <button type="button" className="global-info-btn ghost" disabled={!!loading} onClick={loadCatalog}>刷新目录</button>
            </div>

            <div className="global-info-preset-row">
              {PRESET_APIS.map((p) => (
                <button key={p.api_name} type="button" className={apiName === p.api_name ? 'active' : ''} onClick={() => applyPreset(p)}>
                  {p.title}
                </button>
              ))}
            </div>

            <div className="global-info-quant-grid">
              <div className="global-info-catalog">
                <input
                  className="positions-input"
                  value={catalogQ}
                  onChange={(e) => setCatalogQ(e.target.value)}
                  placeholder="搜索接口名 / 标题 / 分类"
                />
                <div className="global-info-catalog-list">
                  {filteredCatalog.map((it) => (
                    <button
                      key={it.api_name}
                      type="button"
                      className={apiName === it.api_name ? 'active' : ''}
                      onClick={() => applyCatalogItem(it)}
                    >
                      <strong>{it.title || it.api_name}</strong>
                      <span>{it.api_name}</span>
                      <em>{it.category}</em>
                    </button>
                  ))}
                  {!filteredCatalog.length && <div className="global-info-empty">无匹配接口</div>}
                </div>
              </div>

              <div className="global-info-quant-form">
                <label>
                  api_name
                  <input className="positions-input" value={apiName} onChange={(e) => setApiName(e.target.value)} />
                </label>
                <label>
                  params（JSON）
                  <textarea value={paramsText} onChange={(e) => setParamsText(e.target.value)} rows={12} />
                </label>
                <button type="button" className="global-info-btn" disabled={!stockReady || !!loading} onClick={runStockQuery}>
                  查询
                </button>
                {stockError && <div className="global-info-error">{stockError}</div>}
              </div>
            </div>
          </section>

          <section className="global-info-panel">
            <div className="global-info-panel-head">
              <div>
                <h2>查询结果</h2>
                <p>{rows.length ? `共 ${rows.length} 行` : '暂无数据'}</p>
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="global-info-empty">填写 Token 并查询后，结果会显示在这里。</div>
            ) : (
              <div className="global-info-table-wrap">
                <table className="global-info-table">
                  <thead>
                    <tr>
                      {fields.map((f) => <th key={f}>{f}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 200).map((row, idx) => (
                      <tr key={idx}>
                        {fields.map((f) => <td key={f}>{row[f] == null ? '' : String(row[f])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {stockResult && rows.length === 0 && (
              <pre className="global-info-json">{JSON.stringify(stockResult, null, 2)}</pre>
            )}
          </section>
        </>
      )}

      {board === 'usage' && (
        <section className="global-info-panel">
          <div className="global-info-panel-head">
            <div>
              <h2>Token 用量</h2>
              <p>闪讯与量化两套配额互不影响，进度条越满表示越接近上限。</p>
            </div>
            <button type="button" className="global-info-btn" disabled={(!skillReady && !stockReady) || !!loading} onClick={loadUsage}>刷新用量</button>
          </div>
          <div className="global-info-usage-grid">
            <SkillUsagePanel data={usage} ready={skillReady} />
            <StockUsagePanel data={stockUsage} ready={stockReady} />
          </div>
        </section>
      )}
    </div>
  );
}

function formatCount(n) {
  // 注意：Number(null) === 0，不能把空值当成已用 0
  if (n == null || n === '') return '-';
  const v = Number(n);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString('zh-CN');
}

function pickNum(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    if (typeof v === 'boolean') continue;
    if (typeof v === 'object') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function quotaTone(pct) {
  if (pct == null) return 'muted';
  if (pct >= 90) return 'danger';
  if (pct >= 70) return 'warn';
  return 'ok';
}

function QuotaBar({ label, used, limit, start }) {
  const u = pickNum(used);
  const lim = pickNum(limit);
  const hasLimit = lim != null && lim > 0;
  const pct = hasLimit && u != null ? Math.min(100, Math.round((u / lim) * 1000) / 10) : null;
  const remain = hasLimit && u != null ? Math.max(0, lim - u) : null;
  let right = '-';
  if (u != null && hasLimit) right = `${formatCount(u)} / ${formatCount(lim)}${pct != null ? ` · ${pct}%` : ''}`;
  else if (u != null) right = `已用 ${formatCount(u)}`;
  else if (hasLimit) right = `上限 ${formatCount(lim)}`;
  let tip = '暂无配额数据';
  if (remain != null) tip = `剩余 ${formatCount(remain)}`;
  else if (hasLimit) tip = '接口未返回已用量，仅显示上限';
  if (start) tip = `${tip} · 窗口起 ${formatTime(start)}`;
  return (
    <div className={`global-info-quota ${quotaTone(pct)}`}>
      <div className="global-info-quota-top">
        <strong>{label}</strong>
        <span>{right}</span>
      </div>
      <div className="global-info-quota-track">
        <i style={{ width: `${pct ?? 0}%` }} />
      </div>
      <small>{tip}</small>
    </div>
  );
}

function maskToken(token) {
  const s = String(token || '');
  if (s.length <= 10) return s || '-';
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function unwrapUsageRoot(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.error) return data;
  // 可能套多层 { success, data }
  let cur = data;
  for (let i = 0; i < 3; i += 1) {
    if (cur.data && typeof cur.data === 'object' && !Array.isArray(cur.data)
      && (cur.success != null || cur.timestamp != null || cur.error != null)) {
      cur = cur.data;
      continue;
    }
    break;
  }
  return cur;
}

function skillQuotaTriples(root) {
  const limits = root?.limits || root?.quota || root?.global_limits || root?.rate_limits || {};
  // 闪讯真实结构：已用量在 global.hour.count，上限在 limits.hour
  const usage = root?.global || root?.usage || root?.used || root?.counters || root?.current
    || root?.global_usage || root?.usage_stats || root?.stats || {};

  const readPair = (keyAliases) => {
    let used = null;
    let limit = null;
    for (const key of keyAliases) {
      const fromUsage = usage?.[key];
      const fromLimits = limits?.[key];
      const fromRoot = root?.[key];
      if (fromUsage && typeof fromUsage === 'object') {
        used = pickNum(used, fromUsage.count, fromUsage.used, fromUsage.value, fromUsage.total, fromUsage.requests);
        limit = pickNum(limit, fromUsage.limit, fromUsage.max, fromUsage.quota);
      } else {
        used = pickNum(used, fromUsage);
      }
      if (fromLimits && typeof fromLimits === 'object') {
        used = pickNum(used, fromLimits.used, fromLimits.count, fromLimits.value, fromLimits.total);
        limit = pickNum(limit, fromLimits.limit, fromLimits.max, fromLimits.quota);
      } else {
        limit = pickNum(limit, fromLimits);
      }
      if (fromRoot && typeof fromRoot === 'object' && !Array.isArray(fromRoot)) {
        used = pickNum(used, fromRoot.count, fromRoot.used, fromRoot.value);
        limit = pickNum(limit, fromRoot.limit, fromRoot.max);
      }
    }
    return { used, limit };
  };

  const hour = readPair(['hour', 'hourly', 'h', 'hour_count', 'hour_usage']);
  const day = readPair(['day', 'daily', 'd', 'day_count', 'day_usage']);
  const month = readPair(['month', 'monthly', 'm', 'month_count', 'month_usage']);

  hour.used = pickNum(hour.used, root.hour_used, root.used_hour, root.requests_hour, root.hour_count);
  day.used = pickNum(day.used, root.day_used, root.used_day, root.requests_day, root.day_count);
  month.used = pickNum(month.used, root.month_used, root.used_month, root.requests_month, root.month_count);
  hour.limit = pickNum(hour.limit, root.hour_limit, root.limit_hour, root.max_hour);
  day.limit = pickNum(day.limit, root.day_limit, root.limit_day, root.max_day);
  month.limit = pickNum(month.limit, root.month_limit, root.limit_month, root.max_month);

  return [
    { label: '小时配额', used: hour.used, limit: hour.limit, start: usage?.hour?.start || usage?.hourly?.start },
    { label: '日配额', used: day.used, limit: day.limit, start: usage?.day?.start || usage?.daily?.start },
    { label: '月配额', used: month.used, limit: month.limit, start: usage?.month?.start || usage?.monthly?.start },
  ];
}

function endpointQuotaRows(root) {
  // 闪讯：上限在 endpoint_limits，已用量在 endpoint[path].hour.count
  const limitsMap = root?.endpoint_limits || root?.endpoints || {};
  const usageMap = root?.endpoint || root?.endpoint_usage || root?.endpoint_used || root?.endpoints_usage || {};
  const paths = new Set([
    ...Object.keys(limitsMap && typeof limitsMap === 'object' && !Array.isArray(limitsMap) ? limitsMap : {}),
    ...Object.keys(usageMap && typeof usageMap === 'object' && !Array.isArray(usageMap) ? usageMap : {}),
  ]);
  if (!paths.size) return [];

  const limitOf = (v, ...alts) => {
    if (v && typeof v === 'object') return pickNum(v.limit, v.max, v.quota, v.count, ...alts);
    return pickNum(v, ...alts);
  };
  const usedOf = (bucket, ...alts) => {
    if (bucket && typeof bucket === 'object') {
      return pickNum(bucket.count, bucket.used, bucket.value, bucket.total, bucket.requests, ...alts);
    }
    return pickNum(bucket, ...alts);
  };

  return [...paths].map((path) => {
    const c = (limitsMap?.[path] && typeof limitsMap[path] === 'object') ? limitsMap[path] : {};
    const u = (usageMap?.[path] && typeof usageMap[path] === 'object') ? usageMap[path] : {};
    // endpoint_limits 里 hour/day/month 是数字上限；endpoint 里是 { start, count }
    const hourLimitIsObj = c.hour && typeof c.hour === 'object';
    const dayLimitIsObj = c.day && typeof c.day === 'object';
    const monthLimitIsObj = c.month && typeof c.month === 'object';
    return {
      path,
      hour: hourLimitIsObj ? limitOf(c.hour) : pickNum(c.hour, c.hourly, c.h, c.limit_hour),
      day: dayLimitIsObj ? limitOf(c.day) : pickNum(c.day, c.daily, c.d, c.limit_day),
      month: monthLimitIsObj ? limitOf(c.month) : pickNum(c.month, c.monthly, c.m, c.limit_month),
      usedHour: usedOf(u.hour, u.hourly, hourLimitIsObj ? c.hour : null, c.used_hour, c.hour_used, c.usage?.hour),
      usedDay: usedOf(u.day, u.daily, dayLimitIsObj ? c.day : null, c.used_day, c.day_used, c.usage?.day),
      usedMonth: usedOf(u.month, u.monthly, monthLimitIsObj ? c.month : null, c.used_month, c.month_used, c.usage?.month),
      startHour: u.hour?.start || u.hourly?.start,
      startDay: u.day?.start || u.daily?.start,
      startMonth: u.month?.start || u.monthly?.start,
    };
  }).sort((a, b) => a.path.localeCompare(b.path));
}

function SkillUsagePanel({ data, ready }) {
  if (!ready) return <div className="global-info-usage-card"><h3>闪讯用量</h3><div className="global-info-empty">保存闪讯 Token 后点击刷新。</div></div>;
  if (!data) return <div className="global-info-usage-card"><h3>闪讯用量</h3><div className="global-info-empty">点击右上角「刷新用量」。</div></div>;
  if (data.error) return <div className="global-info-usage-card"><h3>闪讯用量</h3><div className="global-info-error">{data.error}</div></div>;

  const root = unwrapUsageRoot(data) || {};
  const quotas = skillQuotaTriples(root);
  const endpoints = endpointQuotaRows(root);
  const name = pickText(root.name, root.token_name, root.label, '未命名');
  const token = pickText(root.token, root.token_id, root.id);
  const isAdmin = root.admin === true || root.is_admin === true;
  const hasAnyUsed = quotas.some((q) => q.used != null) || endpoints.some((ep) => ep.usedHour != null || ep.usedDay != null || ep.usedMonth != null);
  const hasAnyLimit = quotas.some((q) => q.limit != null) || endpoints.some((ep) => ep.hour != null || ep.day != null || ep.month != null);

  return (
    <div className="global-info-usage-card">
      <div className="global-info-usage-card-head">
        <div>
          <h3>闪讯用量</h3>
          <p>skill.xxbs.club · /token/usage</p>
        </div>
        <em className={`global-info-pill ${isAdmin ? 'warn' : 'ok'}`}>{isAdmin ? '管理员' : '普通 Token'}</em>
      </div>

      <div className="global-info-stat-row">
        <div className="global-info-stat"><span>名称</span><strong>{name}</strong></div>
        <div className="global-info-stat"><span>Token</span><strong title={token}>{maskToken(token)}</strong></div>
      </div>

      {!hasAnyUsed && hasAnyLimit && (
        <div className="global-info-empty soft" style={{ marginBottom: 14 }}>
          接口已返回配额上限，但未带「已用量」计数。看板能拉到新闻只说明 Token 可用；`/meta/channels` 不计入限流。点「刷新用量」看最新计数。
        </div>
      )}

      <div className="global-info-quota-list">
        {quotas.map((q) => <QuotaBar key={q.label} {...q} />)}
      </div>

      <div className="global-info-usage-subhead">
        <h4>端点配额</h4>
        <span>{endpoints.length} 个</span>
      </div>
      {endpoints.length === 0 ? (
        <div className="global-info-empty soft">暂无端点配额明细</div>
      ) : (
        <div className="global-info-endpoint-list">
          {endpoints
            .slice()
            .sort((a, b) => (pickNum(b.usedDay, b.usedHour, 0) || 0) - (pickNum(a.usedDay, a.usedHour, 0) || 0))
            .slice(0, 24)
            .map((ep) => (
            <div key={ep.path} className="global-info-endpoint-item">
              <code>{ep.path}</code>
              <div className="global-info-endpoint-meta">
                <span>时 {formatCount(ep.usedHour)}/{formatCount(ep.hour)}</span>
                <span>日 {formatCount(ep.usedDay)}/{formatCount(ep.day)}</span>
                <span>月 {formatCount(ep.usedMonth)}/{formatCount(ep.month)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <details className="global-info-raw-details">
        <summary>查看原始 JSON</summary>
        <pre className="global-info-json">{JSON.stringify(root, null, 2)}</pre>
      </details>
    </div>
  );
}

function StockUsagePanel({ data, ready }) {
  if (!ready) return <div className="global-info-usage-card"><h3>量化用量</h3><div className="global-info-empty">保存量化 Token 后点击刷新。</div></div>;
  if (!data) return <div className="global-info-usage-card"><h3>量化用量</h3><div className="global-info-empty">点击右上角「刷新用量」。</div></div>;
  if (data.error) return <div className="global-info-usage-card"><h3>量化用量</h3><div className="global-info-error">{data.error}</div></div>;

  const root = unwrapUsageRoot(data) || {};
  const today = pickNum(root.requests_today, root.today, root.day_count, 0) ?? 0;
  const lastMin = pickNum(root.requests_last_minute, root.last_minute, 0) ?? 0;
  const limMin = pickNum(root.limit_per_minute, root.minute_limit, 60) ?? 60;
  const remainMin = pickNum(root.remaining_this_minute, limMin - lastMin) ?? Math.max(0, limMin - lastMin);
  const byApi = root.by_api_today && typeof root.by_api_today === 'object' ? Object.entries(root.by_api_today) : [];
  byApi.sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0));
  const recent = Array.isArray(root.recent) ? root.recent : (Array.isArray(root.history) ? root.history : []);

  return (
    <div className="global-info-usage-card">
      <div className="global-info-usage-card-head">
        <div>
          <h3>量化用量</h3>
          <p>stock-data.xxbs.club · /v1/me/usage</p>
        </div>
        <em className={`global-info-pill ${remainMin <= 5 ? 'danger' : 'ok'}`}>
          {remainMin <= 5 ? '接近限速' : '状态正常'}
        </em>
      </div>

      <div className="global-info-stat-row three">
        <div className="global-info-stat"><span>今日请求</span><strong>{formatCount(today)}</strong></div>
        <div className="global-info-stat"><span>近 1 分钟</span><strong>{formatCount(lastMin)}</strong></div>
        <div className="global-info-stat"><span>本分钟剩余</span><strong>{formatCount(remainMin)} / {formatCount(limMin)}</strong></div>
      </div>

      <QuotaBar label="分钟限速占用" used={lastMin} limit={limMin} />

      <div className="global-info-usage-subhead">
        <h4>今日接口分布</h4>
        <span>{byApi.length} 种</span>
      </div>
      {byApi.length === 0 ? (
        <div className="global-info-empty soft">今日尚无调用记录</div>
      ) : (
        <div className="global-info-api-bars">
          {byApi.slice(0, 8).map(([name, count]) => {
            const max = Math.max(...byApi.map(([, c]) => Number(c) || 0), 1);
            const pct = Math.round(((Number(count) || 0) / max) * 100);
            return (
              <div key={name} className="global-info-api-bar">
                <div className="global-info-api-bar-top">
                  <code>{name}</code>
                  <span>{formatCount(count)}</span>
                </div>
                <div className="global-info-quota-track"><i style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}

      <div className="global-info-usage-subhead">
        <h4>最近请求</h4>
        <span>{recent.length} 条</span>
      </div>
      {recent.length === 0 ? (
        <div className="global-info-empty soft">暂无最近请求</div>
      ) : (
        <div className="global-info-recent-wrap">
          <table className="global-info-recent-table">
            <thead>
              <tr>
                <th>时间</th>
                <th>接口</th>
                <th>状态</th>
                <th>耗时</th>
                <th>行数</th>
              </tr>
            </thead>
            <tbody>
              {recent.slice(0, 20).map((item, idx) => {
                const status = pickNum(item.http_status, item.status, 0);
                const ok = item.allowed !== false && !item.rate_limited && status >= 200 && status < 400;
                return (
                  <tr key={item.id || `${item.timestamp}-${idx}`}>
                    <td>{formatTime(item.timestamp || item.time || item.created_at)}</td>
                    <td><code>{pickText(item.api_name, item.path, '-')}</code></td>
                    <td>
                      <em className={`global-info-status ${ok ? 'ok' : 'bad'}`}>
                        {item.rate_limited ? '限速' : (status || (ok ? 'OK' : '失败'))}
                      </em>
                    </td>
                    <td>{pickNum(item.latency_ms, item.latency) != null ? `${pickNum(item.latency_ms, item.latency)} ms` : '-'}</td>
                    <td>{formatCount(pickNum(item.row_count, item.rows, item.count))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function shortAddr(addr) {
  const s = String(addr || '');
  if (s.length <= 14) return s || '-';
  return `${s.slice(0, 8)}…${s.slice(-6)}`;
}

function contextMessages(item) {
  const ctx = item?.context ?? item?.messages ?? item?.nearby ?? item?.surrounding;
  if (Array.isArray(ctx)) return ctx;
  if (ctx && typeof ctx === 'object') {
    const nested = ctx.messages || ctx.items || ctx.list;
    if (Array.isArray(nested)) return nested;
  }
  return [];
}

function parseCaStats(caInfo) {
  if (!caInfo || typeof caInfo !== 'object') return null;
  const fd = caInfo.formatted_data || caInfo.formatted || '';
  const text = typeof fd === 'string' ? fd : '';
  const pick = (...keys) => {
    for (const k of keys) {
      const v = caInfo[k];
      if (v != null && String(v).trim()) return String(v).trim();
    }
    return '';
  };
  const fromText = (re) => {
    const m = text.match(re);
    return m ? m[1].trim() : '';
  };
  return {
    name: pick('name', 'token_name', 'symbol', 'token') || fromText(/\$?([A-Za-z0-9_]{2,20})\b/) || '未知代币',
    chain: pick('chain', 'network', 'chain_id') || fromText(/\b(bsc|eth|sol|base|arb|polygon|robinhood)\b/i),
    price: pick('price', 'price_usd', 'priceUsd') || fromText(/\$?\s*([\d.]+)\s*(?:USD|美元)?/i) || fromText(/价格[:：]?\s*\$?([\d.]+)/),
    change: pick('change_24h', 'pct_24h', 'change') || fromText(/([+-]?\d+(?:\.\d+)?%)/),
    mcap: pick('market_cap', 'mc', 'mcap', 'fdv') || fromText(/市值[:：]?\s*(\$?[\d.]+[KMB万亿]?)/i),
    holders: pick('holders', 'holder', 'holder_count') || fromText(/持有人[:：]?\s*([\d,]+)/),
    liquidity: pick('liquidity', 'liq') || fromText(/流动性[:：]?\s*(\$?[\d.]+[KMB万亿]?)/i),
    address: pick('address', 'ca', 'contract', 'token_address'),
    queriedAt: pick('queried_at', 'updated_at', 'time'),
    rawFormatted: text,
  };
}

function msgSpeaker(m) {
  return pickText(m?.speaker, m?.username, m?.user, m?.name, m?.from, m?.sender, '未知');
}

function msgText(m) {
  return pickText(m?.text, m?.message, m?.content, m?.body, '');
}

function tryParseJsonObject(value) {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text.startsWith('{') && !text.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {}
  return null;
}

function normalizeChatSummary(item, index = 0) {
  const raw = item && typeof item === 'object' ? item : {};
  const nestedFromSummary = tryParseJsonObject(raw.summary)
    || tryParseJsonObject(raw.content)
    || tryParseJsonObject(raw.text)
    || tryParseJsonObject(raw.analysis);
  const root = nestedFromSummary && (nestedFromSummary.analysis || nestedFromSummary.projects || nestedFromSummary.group_name)
    ? { ...raw, ...nestedFromSummary }
    : raw;
  const analysis = (root.analysis && typeof root.analysis === 'object')
    ? root.analysis
    : (tryParseJsonObject(root.analysis) || {});

  const projectsRaw = Array.isArray(analysis.projects)
    ? analysis.projects
    : (Array.isArray(root.projects) ? root.projects : []);
  const projects = projectsRaw
    .map((p) => {
      if (!p) return null;
      if (typeof p === 'string') return { name: p, summary: '' };
      const name = pickText(p.name, p.project, p.token, p.symbol, p.title);
      const summary = pickText(p.summary, p.desc, p.description, p.content, p.reason, p.detail);
      if (!name && !summary) return null;
      return { name: name || '未命名', summary };
    })
    .filter(Boolean);

  const score = analysis.score ?? root.score ?? root.importance ?? null;
  const sentiment = pickText(
    analysis.overall_sentiment,
    root.overall_sentiment,
    analysis.sentiment,
    root.sentiment,
  );
  const overview = pickText(
    analysis.overview,
    analysis.summary,
    root.overview,
    typeof root.summary === 'string' && !root.summary.trim().startsWith('{') ? root.summary : '',
    typeof root.content === 'string' && !root.content.trim().startsWith('{') ? root.content : '',
    typeof root.text === 'string' && !root.text.trim().startsWith('{') ? root.text : '',
  );
  const messageCount = analysis.message_count ?? root.message_count ?? root.msg_count ?? null;
  const group = pickText(root.group_name, root.group, root.title, root.channel, `摘要 ${index + 1}`);
  const when = formatTime(
    root.processed_at
    || root.window_end
    || root.window_start
    || root.created_at
    || root.updated_at
    || root.time
    || analysis.processed_at,
  );

  return {
    id: root.id || `${group}-${when}-${index}`,
    group,
    when,
    whenRaw: root.processed_at
      || root.window_end
      || root.window_start
      || root.created_at
      || root.updated_at
      || root.time
      || analysis.processed_at
      || null,
    score,
    sentiment,
    overview,
    messageCount,
    projects,
    hasStructured: projects.length > 0 || !!overview || score != null || !!sentiment,
  };
}

function chatSummaryDedupeKey(item, index = 0) {
  const n = normalizeChatSummary(item, index);
  const projectsKey = n.projects.map((p) => `${p.name}\u0001${p.summary}`).join('\u0002');
  return [
    n.group,
    n.when,
    String(n.score ?? ''),
    n.sentiment,
    String(n.messageCount ?? ''),
    n.overview,
    projectsKey,
  ].join('\u0000');
}

function summaryTimeMs(item) {
  const n = normalizeChatSummary(item, 0);
  const raw = n.whenRaw;
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return raw < 1e12 ? raw * 1000 : raw;
  const t = Date.parse(String(raw));
  return Number.isNaN(t) ? 0 : t;
}

/** 上游常返回同群同时段重复摘要，展示前按内容指纹去重，并按时间新→旧排序 */
function dedupeChatSummaries(list) {
  if (!Array.isArray(list) || !list.length) return [];
  const seen = new Set();
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const item = list[i];
    const key = chatSummaryDedupeKey(item, i);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  out.sort((a, b) => summaryTimeMs(b) - summaryTimeMs(a));
  return out;
}

function scoreTone(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'muted';
  if (n >= 7) return 'hot';
  if (n >= 4) return 'warm';
  return 'cool';
}

function ChatSummaryCard({ item, index }) {
  const [showRaw, setShowRaw] = useState(false);
  const data = useMemo(() => normalizeChatSummary(item, index), [item, index]);

  return (
    <article className="global-info-summary-card">
      <header>
        <div className="global-info-summary-title">
          <strong>{data.group}</strong>
          <span>{data.when}</span>
        </div>
        <div className="global-info-summary-badges">
          {data.score != null && data.score !== '' && (
            <em className={`summary-badge score-${scoreTone(data.score)}`}>热度 {String(data.score)}</em>
          )}
          {data.sentiment && <em className="summary-badge sentiment">情绪 {data.sentiment}</em>}
          {data.messageCount != null && <em className="summary-badge">消息 {String(data.messageCount)}</em>}
        </div>
      </header>

      {data.overview && <p className="global-info-summary-overview">{data.overview}</p>}

      {data.projects.length > 0 ? (
        <ul className="global-info-summary-projects">
          {data.projects.map((project, i) => (
            <li key={`${project.name}-${i}`}>
              <b>{project.name}</b>
              {project.summary ? <span>{project.summary}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        !data.overview && (
          <p className="global-info-summary-empty">暂无结构化项目摘要</p>
        )
      )}

      <footer className="global-info-summary-footer">
        <button type="button" className="summary-raw-toggle" onClick={() => setShowRaw((v) => !v)}>
          {showRaw ? '收起原始数据' : '查看原始数据'}
        </button>
      </footer>
      {showRaw && (
        <pre className="global-info-summary-raw">{JSON.stringify(item, null, 2)}</pre>
      )}
    </article>
  );
}

function ChatRecordHit({ item, index }) {
  const [showRaw, setShowRaw] = useState(false);
  const group = pickText(item.group, item.group_name, item.chat_group, '未知群');
  const query = pickText(item.query, item.ca, item.url, item.address, '-');
  const matchType = pickText(item.match_type, item.type, item.detect_rule, '');
  const sender = pickText(item.sender, item.username, item.speaker, item.user, '-');
  const when = formatTime(item.time || item.created_at || item.detect_time || item.timestamp);
  const messages = contextMessages(item);
  const caStats = parseCaStats(item.ca_info || item.token_info || item.contract_info);
  const caAddr = caStats?.address || (String(query).startsWith('0x') ? query : '');
  const chain = (caStats?.chain || 'bsc').toLowerCase();
  const gmgnUrl = caAddr ? `https://gmgn.ai/${chain === 'binance' ? 'bsc' : chain}/token/${caAddr}` : '';
  const hitText = pickText(item.message_text, item.text, item.content, '');

  return (
    <article className="chat-hit-card">
      <header className="chat-hit-head">
        <div>
          <strong>{group}</strong>
          <span>命中 #{index + 1}{matchType ? ` · ${matchType}` : ''}</span>
        </div>
        <div className="chat-hit-meta">
          <em>{sender}</em>
          <time>{when}</time>
        </div>
      </header>

      <div className="chat-hit-query">
        <span>查询</span>
        <code title={query}>{shortAddr(query)}</code>
        {gmgnUrl && <a href={gmgnUrl} target="_blank" rel="noreferrer">GMGN</a>}
        {caAddr && (
          <button
            type="button"
            className="chat-hit-copy"
            onClick={() => navigator.clipboard?.writeText(caAddr)}
          >
            复制 CA
          </button>
        )}
      </div>

      {caStats && (
        <div className="chat-token-card">
          <div className="chat-token-title">
            <strong>{caStats.name}</strong>
            {caStats.chain && <em>{caStats.chain}</em>}
          </div>
          <div className="chat-token-grid">
            {caStats.price && <div><span>价格</span><strong>${caStats.price.replace(/^\$/, '')}</strong></div>}
            {caStats.change && <div><span>涨跌</span><strong className={String(caStats.change).includes('-') ? 'down' : 'up'}>{caStats.change}</strong></div>}
            {caStats.mcap && <div><span>市值</span><strong>{caStats.mcap}</strong></div>}
            {caStats.holders && <div><span>持有人</span><strong>{caStats.holders}</strong></div>}
            {caStats.liquidity && <div><span>流动性</span><strong>{caStats.liquidity}</strong></div>}
          </div>
          {caStats.queriedAt && <small>快照时间 {formatTime(caStats.queriedAt)}</small>}
        </div>
      )}

      {messages.length > 0 ? (
        <div className="chat-bubble-list">
          <div className="chat-bubble-label">上下文对话 · {messages.length} 条</div>
          {messages.map((m, i) => {
            const text = msgText(m);
            if (!text) return null;
            const isHit = String(m?.type || '').toLowerCase().includes('ca')
              || String(text).toLowerCase().includes(String(query).toLowerCase().slice(0, 10));
            return (
              <div key={`${msgSpeaker(m)}-${i}`} className={`chat-bubble ${isHit ? 'hit' : ''}`}>
                <div className="chat-bubble-top">
                  <strong>{msgSpeaker(m)}</strong>
                  <span>{formatTime(m.time || m.created_at || m.timestamp)}</span>
                </div>
                <p>{text}</p>
              </div>
            );
          })}
        </div>
      ) : hitText ? (
        <div className="chat-bubble-list">
          <div className="chat-bubble-label">命中消息</div>
          <div className="chat-bubble hit">
            <div className="chat-bubble-top">
              <strong>{sender}</strong>
              <span>{when}</span>
            </div>
            <p>{hitText}</p>
          </div>
        </div>
      ) : (
        <div className="global-info-empty soft">暂无上下文对话</div>
      )}

      <div className="chat-hit-actions">
        <button type="button" className="global-info-btn soft" onClick={() => setShowRaw((v) => !v)}>
          {showRaw ? '收起原始数据' : '查看原始 JSON'}
        </button>
      </div>
      {showRaw && <pre className="chat-hit-raw">{JSON.stringify(item, null, 2)}</pre>}
    </article>
  );
}

function NewsColumn({ title, items, empty, tone }) {
  return (
    <section className={`global-info-news-col ${tone || ''}`}>
      <header>
        <h3>{title}</h3>
        <span>{items.length}</span>
      </header>
      {items.length === 0 ? (
        <div className="global-info-empty">{empty}</div>
      ) : (
        <div className="global-info-news-list">
          {items.map((item, idx) => {
            const href = pickText(item.url, item.link, item.source_url);
            return (
              <article key={item.id || item.url || idx} className="global-info-news-card">
                <h4>
                  {href ? <a href={href} target="_blank" rel="noreferrer">{newsTitle(item)}</a> : newsTitle(item)}
                </h4>
                <p>{newsBody(item)}</p>
                <small>{newsMeta(item)}</small>
                {Array.isArray(item.tokens) && item.tokens.length > 0 && (
                  <div className="global-info-tags">
                    {item.tokens.slice(0, 8).map((t) => <em key={t}>{t}</em>)}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
